import { usePublishedBoxStore } from '@/entities/sets/publish';
import { useCommonStore } from '@/shared/lib/store';
import { DatePicker, InputChk, TabMenu } from '@/shared/ui';
import { publishStateArr, publishTypeArr } from '@/shared/utils/publish';
import moment from 'moment';
import { useEffect, useRef } from 'react';

type ByPublishListFilterProps = {
    filterClassList: API.MyClassMemberList.ClassData[];
    startDate?: Date;
    endDate?: Date;
    dateTab?: string;
    selectClass?: {
        name: string;
        claId: string;
    };
    publishType: number[];
    publishState: number[];
    setSelectClass: (value: { name: string; claId: string }) => void;
    setPublishType: (value: number[]) => void;
    setPublishState: (value: number[]) => void;
    setStartDate: (value: Date | undefined) => void;
    setEndDate: (value: Date | undefined) => void;
    setDateTab: (value: string | undefined) => void;
};

export const ByPublishListFilter = ({
    filterClassList,
    startDate,
    endDate,
    dateTab,
    selectClass,
    publishType,
    publishState,
    setSelectClass,
    setPublishType,
    setPublishState,
    setStartDate,
    setEndDate,
    setDateTab,
}: ByPublishListFilterProps) => {
    const currDate = new Date();
    const { setSearchPage } = usePublishedBoxStore();
    const isFirstDateSelection = useRef(true);
    const { prevPath } = useCommonStore();

    const transformToClassList = (data: API.MyClassMemberList.ClassData[]) => {
        return data.map((item) => {
            const { schName, clsName, classSeq, schCode, crtYear, schYear } = item.classInfo;
            const processedSchName = schName?.includes('중학교')
                ? schName?.replace('중학교', '중')
                : schName?.replace('등학교', '');
            const claId = 'viva-' + schCode + '-' + crtYear + '-' + schYear + '-' + classSeq;
            return {
                name: `${processedSchName} ${clsName}`,
                claId,
            };
        });
    };

    const classList = transformToClassList(filterClassList || []);

    const onDateChange = (date: Date, rangeType: 's' | 'e') => {
        const start = date;
        const end = endDate ?? undefined;
        setDateTab(undefined);

        switch (rangeType) {
            case 's':
                setStartDate(date);

                if (end && end < start) {
                    const newEnd = new Date(start);
                    newEnd.setDate(newEnd.getDate() + 1);
                    setEndDate(newEnd);
                }
                break;
            case 'e':
                setEndDate(date);
                break;
        }
        setSearchPage(0);
    };

    const onDateButton = (type: string) => {
        setDateTab(type);
        const newStart = new Date();
        const current = new Date();
        if (type === '0') {
            setStartDate(undefined);
            setEndDate(undefined);
        } else {
            if (type === '7') {
                newStart.setDate(newStart.getDate() - Number(type));
                setStartDate(newStart);
                setEndDate(current);
            } else {
                const monthStart = moment().subtract(Number(type), 'months').toDate();
                setStartDate(monthStart);
                setEndDate(current);
            }
        }
        if (!isFirstDateSelection.current) setSearchPage(0);
        isFirstDateSelection.current = false;
    };

    useEffect(() => {
        const canRestoreDateRange =
            prevPath.includes('/sets/published-box/detail/') && Boolean(startDate && endDate);

        if (!canRestoreDateRange) onDateButton('3');
        // 최초 진입 시점에만 기존 필터 복원 여부를 판단한다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCheckPublishType = (value: number) => {
        if (value == 0) {
            if (publishType?.length === publishTypeArr.length - 1) {
                setPublishType([]);
            } else {
                setPublishType([1, 2, 3]);
            }
        } else {
            const newList = publishType.includes(value)
                ? publishType?.filter((item) => item !== value)
                : [...publishType, value];

            setPublishType(newList);
        }
        setSearchPage(0);
    };

    const handleCheckPublishState = (value: number) => {
        if (value == 0) {
            if (publishState?.length === publishStateArr.length - 1) {
                setPublishState([]);
            } else {
                setPublishState([1, 2, 3]);
            }
        } else {
            const newList = publishState?.includes(value)
                ? publishState?.filter((item) => item !== value)
                : [...publishState, value];

            setPublishState(newList);
        }
        setSearchPage(0);
    };

    return (
        <div className="table-input table-style1 // con-mt1">
            <table>
                <colgroup>
                    <col style={{ width: '121px' }} />
                    <col style={{ width: '600px' }} />
                    <col style={{ width: '121px' }} />
                    <col />
                </colgroup>
                <tbody>
                    <tr>
                        <th scope="row">용도</th>
                        <td className="chk-gap2">
                            {publishTypeArr.map((item, idx) => (
                                <InputChk
                                    key={item.label}
                                    type="checkbox"
                                    forId={`filterChk2-${idx + 1}`}
                                    onChange={() => handleCheckPublishType(item.value)}
                                    checked={
                                        item.value == 0
                                            ? publishType?.length === publishTypeArr?.length - 1
                                            : publishType?.includes(item.value)
                                    }
                                >
                                    <span className="txt">{item.label}</span>
                                </InputChk>
                            ))}
                        </td>
                        <th scope="row">진행 상태</th>
                        <td className="chk-gap2">
                            {publishStateArr.map((item, idx) => (
                                <InputChk
                                    key={item.label}
                                    type="checkbox"
                                    forId={`filterChk3-${idx + 1}`}
                                    onChange={() => handleCheckPublishState(item.value)}
                                    checked={
                                        item.value == 0
                                            ? publishState?.length === publishStateArr?.length - 1
                                            : publishState?.includes(item.value)
                                    }
                                >
                                    <span className="txt">{item.label}</span>
                                </InputChk>
                            ))}
                        </td>
                    </tr>
                    {publishType?.includes(1) && (
                        <tr>
                            <th scope="row">클래스 선택</th>
                            <td colSpan={3} className="chk-gap1">
                                {classList && classList?.length > 0
                                    ? classList?.map((classItem, idx) => (
                                          <InputChk
                                              key={classItem.claId}
                                              type="radio"
                                              forId={`filterChk1-${idx + 1}`}
                                              checked={
                                                  selectClass &&
                                                  selectClass.claId === classItem.claId
                                              }
                                              onChange={() => setSelectClass(classItem)}
                                          >
                                              <span className="txt">{classItem.name}</span>
                                          </InputChk>
                                      ))
                                    : '아직 발행한 클래스가 없습니다.'}
                            </td>
                        </tr>
                    )}

                    <tr className="row-gap1">
                        <th scope="row">발행일</th>
                        <td colSpan={3}>
                            <div className="datepicker-period">
                                <div className="datepicker">
                                    <DatePicker
                                        placeholderText={'시작 일 선택'}
                                        selectsStart
                                        date={startDate!}
                                        maxDate={endDate}
                                        changed_date={(date: Date) => onDateChange(date, 's')}
                                    />
                                </div>
                                <span className="ico">~</span>
                                <div className="datepicker">
                                    <DatePicker
                                        placeholderText={'종료 일 선택'}
                                        selectsEnd
                                        date={endDate!}
                                        minDate={startDate ? startDate : currDate}
                                        maxDate={new Date('9999-12-31')}
                                        changed_date={(date: Date) => onDateChange(date, 'e')}
                                    />
                                </div>
                            </div>
                            <TabMenu
                                tabType={2}
                                tabItems={[
                                    { tabName: '1주', value: '7' },
                                    { tabName: '1개월', value: '1' },
                                    { tabName: '3개월', value: '3' },
                                ]}
                                selected={dateTab}
                                className="tab-size2"
                                onChange={onDateButton}
                                reset={true}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
